import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EditList } from '../../utils/editing/editList';
import { outputDurationMs, segmentPlacements } from '../../utils/editing/editList';

/**
 * Plays an edit list on one <video> without rendering: the element plays the
 * source, and when it reaches the end of a kept stretch it jumps to the next
 * one (or stops at the end of the result). The clock the screen sees is the
 * result's (output time), the same one overlays, text and captions are timed
 * in. Speed changes set the element's playback rate for that stretch.
 */

export interface PreviewPlayer {
  /** Callback ref for the <video>. */
  ref: (node: HTMLVideoElement | null) => void;
  element: HTMLVideoElement | null;
  /** Output time, ms. */
  outputMs: number;
  /** The result's length, ms. */
  durationMs: number;
  playing: boolean;
  ready: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Seeks in output time. */
  seek: (outputMs: number) => void;
  /** The element's natural size, once known. */
  videoWidth: number;
  videoHeight: number;
  /** The source time the element is at. */
  sourceMs: number;
}

const TICK_MS = 40;

/** Which stretch an output time falls in (the last one includes its end). */
export const placementIndexAt = (placements: ReturnType<typeof segmentPlacements>, outputMs: number): number => {
  for (let i = 0; i < placements.length; i += 1) {
    const { startMs, endMs } = placements[i];
    if (outputMs >= startMs && (outputMs < endMs || (i === placements.length - 1 && outputMs <= endMs))) return i;
  }
  return outputMs < 0 ? 0 : placements.length - 1;
};

export function usePreviewPlayer(list: EditList): PreviewPlayer {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const [outputMs, setOutputMs] = useState(0);
  const [sourceMs, setSourceMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const placements = useMemo(() => segmentPlacements(list), [list]);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const durationMs = useMemo(() => outputDurationMs(list), [list]);
  /** The stretch the element is meant to be in. */
  const indexRef = useRef(0);
  const outputRef = useRef(0);
  const seekingRef = useRef(false);

  const ref = useCallback((node: HTMLVideoElement | null) => setElement(node), []);

  const goTo = useCallback((target: number, el: HTMLVideoElement) => {
    const all = placementsRef.current;
    const total = all.length ? all[all.length - 1].endMs : 0;
    const clamped = Math.max(0, Math.min(total, target));
    const index = placementIndexAt(all, clamped);
    const { segment, startMs } = all[index];
    indexRef.current = index;
    outputRef.current = clamped;
    seekingRef.current = true;
    el.playbackRate = segment.speed;
    el.currentTime = (segment.inMs + (clamped - startMs) * segment.speed) / 1000;
    setOutputMs(clamped);
  }, []);

  useEffect(() => {
    if (!element) return undefined;
    let frame: number | null = null;
    let lastTick = 0;
    const tick = (now: number) => {
      frame = window.requestAnimationFrame(tick);
      if (now - lastTick < TICK_MS) return;
      lastTick = now;
      if (seekingRef.current || element.seeking) return;
      const all = placementsRef.current;
      if (all.length === 0) return;
      const index = Math.min(indexRef.current, all.length - 1);
      const { segment, startMs, endMs } = all[index];
      const currentMs = element.currentTime * 1000;
      setSourceMs(currentMs);
      if (currentMs < segment.outMs) {
        const at = Math.max(startMs, startMs + (currentMs - segment.inMs) / segment.speed);
        outputRef.current = at;
        setOutputMs(at);
        if (element.playbackRate !== segment.speed) element.playbackRate = segment.speed;
        return;
      }
      // Ran past the end of this stretch.
      if (index + 1 < all.length) {
        goTo(all[index + 1].startMs, element);
      } else {
        element.pause();
        outputRef.current = endMs;
        setOutputMs(endMs);
      }
    };
    frame = window.requestAnimationFrame(tick);
    const onSeeked = () => { seekingRef.current = false; };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onMeta = () => {
      setReady(true);
      setSize({ width: element.videoWidth, height: element.videoHeight });
    };
    element.addEventListener('seeked', onSeeked);
    element.addEventListener('play', onPlay);
    element.addEventListener('pause', onPause);
    element.addEventListener('loadedmetadata', onMeta);
    if (element.readyState >= 1) onMeta();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      element.removeEventListener('seeked', onSeeked);
      element.removeEventListener('play', onPlay);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('loadedmetadata', onMeta);
    };
  }, [element, goTo]);

  // When the list changes (a cut, a speed change), stay at the same output time, inside the result.
  useEffect(() => {
    if (!element || !ready) return;
    goTo(outputRef.current, element);
  }, [element, goTo, placements, ready]);

  const seek = useCallback((target: number) => {
    if (element) goTo(target, element);
  }, [element, goTo]);

  const play = useCallback(() => {
    if (!element) return;
    if (outputRef.current >= durationMs - 1) goTo(0, element);
    const result = element.play() as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') result.catch(() => undefined);
  }, [durationMs, element, goTo]);
  const pause = useCallback(() => element?.pause(), [element]);
  const toggle = useCallback(() => (element && element.paused ? play() : pause()), [element, pause, play]);

  return { ref, element, outputMs, durationMs, playing, ready, play, pause, toggle, seek, videoWidth: size.width, videoHeight: size.height, sourceMs };
}
